import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import { getDocs, orderBy, query, where } from "firebase/firestore";
import {
    Ban,
    Copy,
    KeyRound,
    Plus,
    RefreshCw,
    Trash2,
    TriangleAlert,
} from "lucide-react";
import {
    createPartnerApiKey,
    deletePartnerApiKey,
    partnerApiBaseUrl,
    partnerApiKeysCollection,
    partnerApiUsageCollection,
    revokePartnerApiKey,
} from "../utils/firebase";

/* Admin: Partner API keys.
 *
 * Keys are created, revoked and deleted only through admin callables; this
 * page reads partner-api-keys and partner-api-usage directly (admin-only in
 * firestore.rules). The full key exists once, in the create dialog: only a
 * SHA-256 of its secret is stored, so a lost key is revoked and replaced. */

const KEY_NAME_MAX = 60;
const USAGE_DAYS = 30;

// Usage docs are dated in Malaysian time (UTC+8), like the server counts them.
const mytDay = (ms) => new Date(ms + 8 * 3600 * 1000).toISOString().slice(0, 10);

const toDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatWhen = (ts) => {
    const d = toDate(ts);
    if (!d) return "—";
    return d.toLocaleString("en-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const statusOf = (key) => {
    if (key.status === "revoked") return "revoked";
    const exp = toDate(key.expiresAt);
    if (exp && exp.getTime() <= Date.now()) return "expired";
    return "active";
};

const STATUS_BADGE = {
    active: "badge-success",
    revoked: "badge-error",
    expired: "badge-warning",
};

async function copyText(text, what) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${what} copied`);
    } catch (error) {
        console.error(error);
        toast.error("Couldn't copy. Select the text and copy it by hand.");
    }
}

/**
 * A DaisyUI modal. `dismissible` false (the one-time key view) leaves DONE as
 * the only way out: no backdrop click, no Escape.
 */
function Dialog({ id, title, children, onClose, busy, dismissible = true }) {
    const canDismiss = dismissible && !busy;
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            if (canDismiss) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [canDismiss, onClose]);
    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby={id}>
            <div className="modal-box border flex flex-col gap-4">
                <h3 id={id} className="font-bold text-lg">
                    {title}
                </h3>
                {children}
            </div>
            <button
                type="button"
                className="modal-backdrop"
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => canDismiss && onClose()}
            />
        </div>
    );
}

export default function PartnerApi() {
    const [keys, setKeys] = useState(null);
    const [usage, setUsage] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);

    // Create dialog: { name, expires, busy, created: {key, prefix} | null }
    const [create, setCreate] = useState(null);
    // Revoke / delete confirm: { key, busy }
    const [revoking, setRevoking] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const now = Date.now();
            const from = mytDay(now - (USAGE_DAYS - 1) * 86400000);
            const [keySnap, usageSnap] = await Promise.all([
                getDocs(query(partnerApiKeysCollection, orderBy("createdAt", "desc"))),
                getDocs(query(partnerApiUsageCollection, where("date", ">=", from))),
            ]);
            const today = mytDay(now);
            const byKey = {};
            usageSnap.docs.forEach((d) => {
                const u = d.data();
                const row = (byKey[u.keyId] ??= { today: 0, month: 0, limited: 0 });
                row.month += u.requests || 0;
                row.limited += u.rateLimited || 0;
                if (u.date === today) row.today += u.requests || 0;
            });
            setKeys(keySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setUsage(byKey);
        } catch (error) {
            console.error("Loading partner API keys failed:", error);
            setLoadError(true);
            setKeys((prev) => prev ?? []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => {
        const c = { active: 0, revoked: 0, expired: 0 };
        (keys ?? []).forEach((k) => {
            c[statusOf(k)] += 1;
        });
        return c;
    }, [keys]);

    const submitCreate = async (e) => {
        e.preventDefault();
        const name = create.name.replace(/\s+/g, " ").trim();
        if (!name || create.busy) return;
        setCreate((c) => ({ ...c, busy: true }));
        try {
            const res = await createPartnerApiKey({
                name,
                // End of the chosen day, Malaysian time.
                expiresAt: create.expires ? `${create.expires}T23:59:59+08:00` : null,
            });
            if (!res?.success) {
                toast.error(res?.error || "Couldn't create the key.");
                setCreate((c) => ({ ...c, busy: false }));
                return;
            }
            setCreate((c) => ({ ...c, busy: false, created: { key: res.key, prefix: res.prefix } }));
            load();
        } catch (error) {
            console.error("createPartnerApiKey failed:", error);
            toast.error("Couldn't create the key. Try again.");
            setCreate((c) => ({ ...c, busy: false }));
        }
    };

    const confirmRevoke = async () => {
        setRevoking((r) => ({ ...r, busy: true }));
        try {
            const res = await revokePartnerApiKey({ id: revoking.key.id });
            if (!res?.success) throw new Error(res?.error || "Couldn't revoke the key.");
            toast.success(`${revoking.key.name} revoked. It stops working on its next request.`);
            setRevoking(null);
            load();
        } catch (error) {
            toast.error(error.message || "Couldn't revoke the key.");
            setRevoking((r) => ({ ...r, busy: false }));
        }
    };

    const confirmDelete = async () => {
        setDeleting((d) => ({ ...d, busy: true }));
        try {
            const res = await deletePartnerApiKey({ id: deleting.key.id });
            if (!res?.success) throw new Error(res?.error || "Couldn't delete the key.");
            toast.success(`${deleting.key.name} deleted.`);
            setDeleting(null);
            load();
        } catch (error) {
            toast.error(error.message || "Couldn't delete the key.");
            setDeleting((d) => ({ ...d, busy: false }));
        }
    };

    if (!keys) {
        return (
            <div className="page-size flex-center">
                <div className="loading loading-md loading-bars" />
            </div>
        );
    }

    const minExpiry = mytDay(Date.now() + 86400000);

    return (
        <div className="page-size flex flex-col gap-4 p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                <div>
                    <p className="font-bold text-xl">Partner API</p>
                    <p className="text-sm opacity-70">
                        Read-only access to events, matches, fighters and judges' scorecards for the
                        prediction-market partner. {counts.active} active
                        {counts.revoked ? ` · ${counts.revoked} revoked` : ""}
                        {counts.expired ? ` · ${counts.expired} expired` : ""}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={load}
                        disabled={loading}
                        title="Refresh"
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setCreate({ name: "", expires: "", busy: false, created: null })}
                    >
                        <Plus size={16} />
                        CREATE KEY
                    </button>
                </div>
            </div>

            <div className="border p-3 flex flex-col md:flex-row md:items-center gap-2">
                <span className="text-xs font-bold tracking-widest uppercase opacity-60 flex-none">Base URL</span>
                <code className="flex-1 min-w-0 text-sm break-all">{partnerApiBaseUrl}</code>
                <button
                    type="button"
                    className="btn btn-ghost btn-xs self-start md:self-auto"
                    onClick={() => copyText(partnerApiBaseUrl, "Base URL")}
                >
                    <Copy size={14} />
                    COPY
                </button>
            </div>

            {loadError && (
                <div className="alert alert-error">
                    <TriangleAlert size={18} />
                    <span>Couldn't load the keys. Check your connection and refresh.</span>
                </div>
            )}

            {keys.length === 0 ? (
                <div className="border p-8 flex flex-col items-center gap-2 text-center">
                    <KeyRound size={32} className="opacity-50" />
                    <p className="font-bold">No keys yet</p>
                    <p className="text-sm opacity-70 max-w-md">
                        Create a key for the partner. You'll see it once; send it to them over a
                        secure channel.
                    </p>
                </div>
            ) : (
                <>
                {/* Phones: one stacked card per key, actions in view. */}
                <div className="flex flex-col gap-2 md:hidden">
                    {keys.map((k) => {
                        const status = statusOf(k);
                        const u = usage[k.id] ?? { today: 0, month: 0, limited: 0 };
                        return (
                            <div
                                key={k.id}
                                className={twMerge("border p-3 flex flex-col gap-2", status !== "active" && "opacity-80")}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-semibold break-words">{k.name}</p>
                                        <code className="text-xs break-all">{k.prefix}…</code>
                                    </div>
                                    <span className={twMerge("badge badge-sm uppercase flex-none", STATUS_BADGE[status])}>
                                        {status}
                                    </span>
                                </div>
                                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                    <dt className="opacity-60">Today</dt>
                                    <dd className="tabular-nums text-right">{u.today.toLocaleString()}</dd>
                                    <dt className="opacity-60">{USAGE_DAYS} days</dt>
                                    <dd className="tabular-nums text-right">
                                        {u.month.toLocaleString()}
                                        {u.limited > 0 && (
                                            <span className="text-warning"> · {u.limited.toLocaleString()} limited</span>
                                        )}
                                    </dd>
                                    <dt className="opacity-60">Last used</dt>
                                    <dd className="text-right">{formatWhen(k.lastUsedAt)}</dd>
                                    <dt className="opacity-60">Created</dt>
                                    <dd className="text-right">
                                        {formatWhen(k.createdAt)}
                                        {k.createdByEmail && (
                                            <span className="block opacity-60 break-all">{k.createdByEmail}</span>
                                        )}
                                    </dd>
                                    {k.expiresAt && (
                                        <>
                                            <dt className="opacity-60">{status === "expired" ? "Expired" : "Expires"}</dt>
                                            <dd className="text-right">{formatWhen(k.expiresAt)}</dd>
                                        </>
                                    )}
                                    {status === "revoked" && (
                                        <>
                                            <dt className="opacity-60">Revoked</dt>
                                            <dd className="text-right">{formatWhen(k.revokedAt)}</dd>
                                        </>
                                    )}
                                </dl>
                                {status === "revoked" ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-error btn-sm w-full"
                                        onClick={() => setDeleting({ key: k, busy: false })}
                                    >
                                        <Trash2 size={14} />
                                        DELETE
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-error btn-sm w-full"
                                        onClick={() => setRevoking({ key: k, busy: false })}
                                    >
                                        <Ban size={14} />
                                        REVOKE
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="overflow-x-auto border hidden md:block">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Key</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Last used</th>
                                <th className="text-right">Today</th>
                                <th className="text-right">{USAGE_DAYS} days</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => {
                                const status = statusOf(k);
                                const u = usage[k.id] ?? { today: 0, month: 0, limited: 0 };
                                return (
                                    <tr key={k.id} className={status !== "active" ? "opacity-70" : ""}>
                                        <td className="font-semibold">
                                            {k.name}
                                            {k.expiresAt && (
                                                <span className="block text-xs font-normal opacity-60">
                                                    {status === "expired" ? "Expired" : "Expires"}{" "}
                                                    {formatWhen(k.expiresAt)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <code className="text-xs whitespace-nowrap">{k.prefix}…</code>
                                        </td>
                                        <td>
                                            <span className={twMerge("badge badge-sm uppercase", STATUS_BADGE[status])}>
                                                {status}
                                            </span>
                                            {status === "revoked" && (
                                                <span className="block text-xs opacity-60 mt-1 whitespace-nowrap">
                                                    {formatWhen(k.revokedAt)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-xs whitespace-nowrap">
                                            {formatWhen(k.createdAt)}
                                            <span className="block opacity-60">{k.createdByEmail || k.createdBy || ""}</span>
                                        </td>
                                        <td className="text-xs whitespace-nowrap">{formatWhen(k.lastUsedAt)}</td>
                                        <td className="text-right tabular-nums">{u.today.toLocaleString()}</td>
                                        <td className="text-right tabular-nums">
                                            {u.month.toLocaleString()}
                                            {u.limited > 0 && (
                                                <span
                                                    className="block text-xs text-warning"
                                                    title="Requests refused by the rate limit"
                                                >
                                                    {u.limited.toLocaleString()} limited
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right whitespace-nowrap">
                                            {status === "revoked" ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs text-error"
                                                    onClick={() => setDeleting({ key: k, busy: false })}
                                                >
                                                    <Trash2 size={14} />
                                                    DELETE
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline btn-error btn-xs"
                                                    onClick={() => setRevoking({ key: k, busy: false })}
                                                >
                                                    <Ban size={14} />
                                                    REVOKE
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            <p className="text-xs opacity-60">
                Usage is counted per key per day in Malaysian time. Each key may make 120 requests a
                minute. Revoking stops a key on its next request and keeps its history; only revoked
                keys can be deleted.
            </p>

            {create && (
                <Dialog
                    id="create-key-title"
                    title={create.created ? "Copy the key now" : "Create a partner key"}
                    busy={create.busy}
                    dismissible={!create.created}
                    onClose={() => setCreate(null)}
                >
                    {create.created ? (
                        <>
                            <div className="alert alert-warning text-sm">
                                <TriangleAlert size={18} className="flex-none" />
                                <span>
                                    This is the only time the full key is shown. Only a hash is stored,
                                    so it can't be shown again. If it's lost, revoke it and create a
                                    new one.
                                </span>
                            </div>
                            <code className="block border p-3 text-sm break-all select-all">
                                {create.created.key}
                            </code>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary flex-1"
                                    onClick={() => copyText(create.created.key, "Key")}
                                >
                                    <Copy size={16} />
                                    COPY KEY
                                </button>
                                <button type="button" className="btn flex-1" onClick={() => setCreate(null)}>
                                    DONE
                                </button>
                            </div>
                        </>
                    ) : (
                        <form className="flex flex-col gap-4" onSubmit={submitCreate}>
                            <label className="form-control w-full">
                                <span className="label-text font-semibold mb-1">Name</span>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="e.g. Partner production"
                                    maxLength={KEY_NAME_MAX}
                                    value={create.name}
                                    onChange={(e) => setCreate((c) => ({ ...c, name: e.target.value }))}
                                    disabled={create.busy}
                                    autoFocus
                                />
                            </label>
                            <label className="form-control w-full">
                                <span className="label-text font-semibold mb-1">Expires (optional)</span>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    min={minExpiry}
                                    value={create.expires}
                                    onChange={(e) => setCreate((c) => ({ ...c, expires: e.target.value }))}
                                    disabled={create.busy}
                                />
                                <span className="label-text-alt opacity-60 mt-1">
                                    Stops working at the end of that day (Malaysian time). Leave empty for no
                                    expiry.
                                </span>
                            </label>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="btn flex-1"
                                    onClick={() => setCreate(null)}
                                    disabled={create.busy}
                                >
                                    CANCEL
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary flex-1"
                                    disabled={!create.name.trim() || create.busy}
                                >
                                    {create.busy ? <span className="loading loading-spinner loading-sm" /> : "CREATE"}
                                </button>
                            </div>
                        </form>
                    )}
                </Dialog>
            )}

            {revoking && (
                <Dialog
                    id="revoke-key-title"
                    title={`Revoke ${revoking.key.name}?`}
                    busy={revoking.busy}
                    onClose={() => setRevoking(null)}
                >
                    <p className="text-sm">
                        <code>{revoking.key.prefix}…</code> stops working on its next request (401
                        KEY_REVOKED). It stays in the list with its usage, and can then be deleted. This
                        can't be undone; the partner would need a new key.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn flex-1"
                            onClick={() => setRevoking(null)}
                            disabled={revoking.busy}
                        >
                            CANCEL
                        </button>
                        <button
                            type="button"
                            className="btn btn-error flex-1"
                            onClick={confirmRevoke}
                            disabled={revoking.busy}
                        >
                            {revoking.busy ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <>
                                    <Ban size={16} />
                                    REVOKE
                                </>
                            )}
                        </button>
                    </div>
                </Dialog>
            )}

            {deleting && (
                <Dialog
                    id="delete-key-title"
                    title={`Delete ${deleting.key.name}?`}
                    busy={deleting.busy}
                    onClose={() => setDeleting(null)}
                >
                    <p className="text-sm">
                        Removes <code>{deleting.key.prefix}…</code> and its usage history for good.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn flex-1"
                            onClick={() => setDeleting(null)}
                            disabled={deleting.busy}
                        >
                            CANCEL
                        </button>
                        <button
                            type="button"
                            className="btn btn-error flex-1"
                            onClick={confirmDelete}
                            disabled={deleting.busy}
                        >
                            {deleting.busy ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <>
                                    <Trash2 size={16} />
                                    DELETE
                                </>
                            )}
                        </button>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
